import React, { useState } from "react";
import Header from "./components/header";
import Footer from "./components/footer";
import { LiveChatWidget } from '@livechat/widget-react';
import { Helmet } from "react-helmet";

const VideoBook = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    message: "",
    requestType: [],
    videoBookQuantity: 0,
    brochureQuantity: "100",
    kitsQuantity: 0,
    bottle36ozQuantity: 0,
    bottle72ozQuantity: 0,
    honeypot: "",
  });

  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "requestType") {
      setFormData((prevData) => ({
        ...prevData,
        requestType: checked
          ? [...prevData.requestType, value]
          : prevData.requestType.filter((item) => item !== value),
      }));
    } else if (type === "number") {
      // Convert to number and enforce limits
      const numValue = parseInt(value) || 0;
      let limitedValue = numValue;
      
      if (name === "videoBookQuantity" && numValue > 25) {
        limitedValue = 25;
      } else if (
        (name === "kitsQuantity" || name === "bottle36ozQuantity" || name === "bottle72ozQuantity") && 
        numValue > 6
      ) {
        limitedValue = 6;
      }
      
      setFormData((prevData) => ({ ...prevData, [name]: limitedValue }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.company.trim()) newErrors.company = "Company is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.zipCode.trim()) newErrors.zipCode = "Zip code is required";
    
    // Request type validation
    if (formData.requestType.length === 0) {
      newErrors.requestType = "Please select at least one request type";
    }
    
    // Quantity validation based on selection
    if (formData.requestType.includes("Video Books") && formData.videoBookQuantity <= 0) {
      newErrors.videoBookQuantity = "Please specify quantity (max 25)";
    }
    
    if (formData.requestType.includes("Brochures") && !formData.brochureQuantity) {
      newErrors.brochureQuantity = "Please select brochure quantity";
    }
    
    if (formData.requestType.includes("iFLO Samples")) {
      const totalSamples = formData.kitsQuantity + formData.bottle36ozQuantity + formData.bottle72ozQuantity;
      if (totalSamples <= 0) {
        newErrors.samples = "Please specify at least one sample quantity";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setStatus("Please correct the errors in the form.");
      return;
    }
    
    if (formData.honeypot !== "") {
      setStatus("Spam detected!");
      return;
    }

    const webhookUrl = "https://hooks.zapier.com/hooks/catch/19433399/204vhip/"; // Replace with your webhook URL

    try {
      const response = await fetch(webhookUrl, {
        method: "POST",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("Request submitted successfully!");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          company: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
          message: "",
          requestType: [],
          videoBookQuantity: 0,
          brochureQuantity: "100",
          kitsQuantity: 0,
          bottle36ozQuantity: 0,
          bottle72ozQuantity: 0,
          honeypot: "",
        });
      } else {
        setStatus("Failed to submit request. Please try again.");
      }
    } catch (error) {
      setStatus("An error occurred. Please try again later.");
    }
  };

  const [visibility, setVisibility] = useState("minimized");

  return (
    <>
      <Helmet>
        <title>iFLO Pro | Request Materials</title>
        <meta name="description" content="Request Video Books, Brochures, and iFLO Samples for your business." />
        <meta name="keywords" content="iFLO Pro, HVAC, Video Books, Brochures, Samples, Request Materials" />
      </Helmet>
      <Header />
      <LiveChatWidget
        license="14746245"
        visibility={visibility}
      />
      <div className="h-full bg-gradient-to-b from-gray-50 to-white text-gray-800 py-36 px-4">
        <div className="w-full max-w-screen-xl mx-auto">
          <div className="flex flex-col w-full md:flex-row gap-12">
            {/* Form Section */}
            <form
              onSubmit={handleSubmit}
              className="space-y-6 md:w-3/4 bg-white rounded-2xl p-8"
            >
              <div className="space-y-2">
                <h2 className="text-4xl font-bold text-gray-900">Request Materials <span className="text-sm text-red-500">(For Sales Reps Only)</span></h2>
                <p className="text-gray-600 text-lg">
                  Request Video Books, Brochures, and iFLO Samples for your business. 
                  Fill out the form below and we'll process your request promptly.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">First name *</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.firstName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Last name *</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.lastName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email address *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Phone number *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.phone ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Company/Business *</label>
                <input
                  type="text"
                  name="company"
                  required
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Shipping Address *</label>
                <input
                  type="text"
                  name="address"
                  placeholder="Enter your street address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full p-4 bg-gray-50 border ${errors.address ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                  required
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">City *</label>
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.city ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">State *</label>
                  <input
                    type="text"
                    name="state"
                    placeholder="State"
                    value={formData.state}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.state ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Zip Code *</label>
                  <input
                    type="text"
                    name="zipCode"
                    placeholder="Zip Code"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.zipCode ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                </div>
              </div>

              <div className="space-y-4">
                <p className="font-semibold text-gray-900">What would you like to request? *</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["Video Books", "Brochures", "iFLO Samples"].map((type) => (
                    <label key={type} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        name="requestType"
                        value={type}
                        checked={formData.requestType.includes(type)}
                        onChange={handleChange}
                        className="w-5 h-5 accent-yellow-500 rounded border-gray-300 focus:ring-yellow-500"
                      />
                      <span className="text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
                {errors.requestType && <p className="text-red-500 text-xs mt-1">{errors.requestType}</p>}
              </div>

              {formData.requestType.includes("Video Books") && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Video Books Quantity *
                    <span className="text-xs text-gray-500 ml-2">(Max 25)</span>
                  </label>
                  <input
                    type="number"
                    name="videoBookQuantity"
                    min="1"
                    max="25"
                    value={formData.videoBookQuantity}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.videoBookQuantity ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  />
                  {errors.videoBookQuantity && <p className="text-red-500 text-xs mt-1">{errors.videoBookQuantity}</p>}
                </div>
              )}

              {formData.requestType.includes("Brochures") && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Brochures Quantity *
                  </label>
                  <select
                    name="brochureQuantity"
                    value={formData.brochureQuantity}
                    onChange={handleChange}
                    className={`w-full p-4 bg-gray-50 border ${errors.brochureQuantity ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                    required
                  >
                    <option value="">Select quantity</option>
                    <option value="100">100 brochures</option>
                    <option value="250">250 brochures</option>
                    <option value="500">500 brochures</option>
                    <option value="box">1 box</option>
                  </select>
                  {errors.brochureQuantity && <p className="text-red-500 text-xs mt-1">{errors.brochureQuantity}</p>}
                </div>
              )}

              {formData.requestType.includes("iFLO Samples") && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      iFLO Samples *
                    </label>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm text-gray-700">Kits (Max 6)</label>
                        <input
                          type="number"
                          name="kitsQuantity"
                          min="0"
                          max="6"
                          value={formData.kitsQuantity}
                          onChange={handleChange}
                          className={`w-full p-4 bg-gray-50 border ${errors.samples ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm text-gray-700">36oz Bottle (Max 6)</label>
                        <input
                          type="number"
                          name="bottle36ozQuantity"
                          min="0"
                          max="6"
                          value={formData.bottle36ozQuantity}
                          onChange={handleChange}
                          className={`w-full p-4 bg-gray-50 border ${errors.samples ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm text-gray-700">72oz Bottle (Max 6)</label>
                        <input
                          type="number"
                          name="bottle72ozQuantity"
                          min="0"
                          max="6"
                          value={formData.bottle72ozQuantity}
                          onChange={handleChange}
                          className={`w-full p-4 bg-gray-50 border ${errors.samples ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all`}
                        />
                      </div>
                    </div>
                    {errors.samples && <p className="text-red-500 text-xs mt-1">{errors.samples}</p>}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Additional Message</label>
                <textarea
                  name="message"
                  placeholder="Any additional information about your request"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all h-32 resize-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                className="hidden"
              />

              <button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                Submit Request
              </button>
              {status && (
                <p className={`mt-4 text-sm ${status.includes("successfully") ? "text-green-600" : "text-red-600"}`}>
                  {status}
                </p>
              )}
            </form>

            {/* Information Section */}
            <div className="md:w-1/3 w-full space-y-8 bg-white rounded-2xl p-8">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">About Our Materials</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-lg mb-2">Video Books</h4>
                    <p className="text-gray-700">
                      Our interactive Video Books showcase iFLO products and their benefits with embedded video screens.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-lg mb-2">Brochures</h4>
                    <p className="text-gray-700">
                      Detailed product brochures with specifications, applications, and installation instructions.
                      <span className="block mt-2 font-medium text-yellow-600">Limited to 500 per request.</span>
                    </p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-lg mb-2">iFLO Samples</h4>
                    <p className="text-gray-700">
                      Physical product samples to demonstrate quality and functionality to your customers.
                      <span className="block mt-2 font-medium text-yellow-600">Limited to 12 per request.</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Need Help?</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => setVisibility("maximized")}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-3"
                  >
                    <span className="text-lg text-gray-700">Start a live chat</span>
                  </button>
                  <a 
                    href="mailto:HVACPros@iflo.com"
                    className="block w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-lg text-gray-700">Email us</span>
                  </a>
                  <a 
                    href="tel:+8889794356"
                    className="block w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-lg text-gray-700">+888-979-iFLO (4356)</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VideoBook;
